import { Space, Tag, Typography } from 'antd';
import type { DescriptionsProps } from 'antd';
import { useTranslation } from 'react-i18next';
import type { GetKmsDocumentStatusResponse } from '~/app/services/kms/kms-document.types';
import {
  formatConfidencePercent,
  formatKmsDocumentTypeLabel,
  formatKmsFileSize,
} from '~/app/services/kms/kms-document.utils';

const { Text } = Typography;

export function useKmsDocumentDetailFields(
  document: GetKmsDocumentStatusResponse | null,
): DescriptionsProps['items'] {
  const { t } = useTranslation('kms');

  if (!document) {
    return [];
  }

  const manualType = document.documentTypeHint?.trim() || null;
  const aiType = document.classifiedDocumentType?.trim() || null;
  const manualDiffers = Boolean(manualType && aiType && manualType !== aiType);

  return [
    {
      key: 'fileName',
      label: t('detail.fields.fileName'),
      children: document.fileName ?? '—',
    },
    {
      key: 'status',
      label: t('detail.fields.status'),
      children: (
        <Space direction="vertical" size={4}>
          <Tag
            color={
              document.status === 'linked'
                ? 'green'
                : document.status === 'pending_review'
                  ? 'orange'
                  : document.status === 'write_failed'
                    ? 'red'
                    : 'blue'
            }
          >
            {document.status}
          </Tag>
          {document.status === 'write_failed' && document.amsWriteError ? (
            <Text type="danger" style={{ maxWidth: 480 }}>
              {document.amsWriteError}
            </Text>
          ) : null}
        </Space>
      ),
    },
    {
      key: 'aiType',
      label: t('detail.fields.aiSuggestion'),
      children: aiType ? (
        <Tag color="blue">{formatKmsDocumentTypeLabel(aiType)}</Tag>
      ) : (
        <Text type="secondary">—</Text>
      ),
    },
    {
      key: 'manualType',
      label: t('detail.fields.manualType'),
      children: manualType ? (
        <Tag color={manualDiffers ? 'orange' : 'default'}>
          {formatKmsDocumentTypeLabel(manualType)}
        </Tag>
      ) : (
        <Text type="secondary">{t('detail.noManualType')}</Text>
      ),
    },
    {
      key: 'confidence',
      label: t('detail.fields.confidence'),
      children: formatConfidencePercent(document.classificationConfidence),
    },
    {
      key: 'scope',
      label: t('detail.fields.scope'),
      children: document.scope ?? '—',
    },
    {
      key: 'asset',
      label: t('detail.fields.linkedAsset'),
      children: document.assetId ? (
        <Text>
          {document.assetName?.trim() || document.assetId}
          {document.assetName ? (
            <>
              {' '}
              <Text type="secondary" code style={{ fontSize: 12 }}>
                {document.assetId}
              </Text>
            </>
          ) : null}
        </Text>
      ) : (
        <Tag>{t('library.orgWideBadge')}</Tag>
      ),
    },
    {
      key: 'mime',
      label: t('detail.fields.mimeType'),
      children: document.fileMimeType ?? '—',
    },
    {
      key: 'size',
      label: t('detail.fields.fileSize'),
      children: formatKmsFileSize(document.fileSize),
    },
    {
      key: 'createdAt',
      label: t('detail.fields.uploadedAt'),
      children: document.createdAt
        ? new Date(document.createdAt).toLocaleString()
        : '—',
    },
    {
      key: 'hitlResolvedAt',
      label: t('detail.fields.reviewedAt'),
      children: document.hitlResolvedAt
        ? new Date(document.hitlResolvedAt).toLocaleString()
        : '—',
    },
    {
      key: 'documentId',
      label: t('detail.fields.documentId'),
      children: (
        <Text copyable code>
          {document.documentId}
        </Text>
      ),
    },
  ];
}
